<template>
  <!-- В body: окно живёт внутри бокового меню, а у того свой контекст наложения
       и прокрутка — оттуда обрез правого края и перекрытие липкой шапкой -->
  <Teleport to="body">
  <div
    v-if="show"
    class="url-modal-overlay"
    @click.self="closeModal"
  >
    <div class="url-modal">
      <div class="url-modal-header">
        <h3>{{ $t('upload_url.title') }}</h3>
        <button class="close-btn" @click="closeModal" :disabled="submitting">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="url-modal-body">
        <!-- Приняли -->
        <div v-if="submitSuccess" class="url-success-state">
          <div class="success-icon"><i class="bi bi-check-circle-fill"></i></div>
          <div class="success-text">{{ $t('upload_url.accepted') }}</div>
          <button
            v-if="materialId"
            class="btn btn-outline-primary mt-3"
            @click="goToMaterial"
          >
            <i class="bi bi-arrow-right me-2"></i>
            {{ $t('upload.go_to_material') }}
          </button>
        </div>

        <template v-else>
          <p class="url-hint">{{ $t('upload_url.hint') }}</p>

          <div class="form-group mb-3">
            <input
              ref="urlInput"
              v-model="url"
              type="url"
              class="form-control url-input"
              :class="{ 'is-invalid': !!errorMessage }"
              :placeholder="$t('upload_url.placeholder')"
              :disabled="submitting"
              @keyup.enter="submit"
            >
          </div>

          <!-- Витрина источников: список приходит с бэкенда, чтобы не разойтись
               с валидатором, который реально принимает ссылку -->
          <div class="sources-block">
            <div class="sources-caption">{{ $t('upload_url.supported') }}</div>
            <ul class="sources-list">
              <li
                v-for="src in sources"
                :key="src.key"
                class="source-chip"
                :class="{ 'source-chip--matched': detectedService === src.key }"
                :title="src.example"
              >
                <i :class="['bi', sourceIcon(src.key)]"></i>
                <span class="source-title">{{ src.title }}</span>
              </li>
            </ul>
          </div>

          <div class="form-group mb-3">
            <input
              v-model="title"
              type="text"
              class="form-control"
              :placeholder="$t('upload_url.title_placeholder')"
              :disabled="submitting"
            >
          </div>

          <div v-if="errorMessage" class="alert alert-danger url-error">{{ errorMessage }}</div>

          <div class="url-modal-actions">
            <button class="btn btn-secondary" @click="closeModal" :disabled="submitting">
              {{ $t('upload_url.cancel') }}
            </button>
            <button class="btn btn-primary" @click="submit" :disabled="submitting || !url.trim()" v-track="{ event: 'upload.submit.click', target: 'url' }">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ submitting ? $t('upload_url.submitting') : $t('upload_url.submit') }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import axiosConfig from '@/lib/axiosConfig';
import { API_URL } from '@/config.js';
import { mapGetters } from 'vuex';

// Запасная витрина: окно без списка источников бесполезнее, чем со слегка
// устаревшим. Живой список приходит из url_sources и перетирает этот.
const FALLBACK_SOURCES = [
  { key: 'youtube', title: 'YouTube', example: 'https://youtu.be/…' },
  { key: 'yandex', title: 'Яндекс.Диск', example: 'https://disk.yandex.ru/i/…' },
  { key: 'vk', title: 'VK Видео', example: 'https://vkvideo.ru/video…' },
  { key: 'soundcloud', title: 'SoundCloud', example: 'https://soundcloud.com/…' },
  { key: 'pinterest', title: 'Pinterest', example: 'https://pin.it/…' }
];

const SOURCE_ICONS = {
  youtube: 'bi-youtube',
  yandex: 'bi-cloud-arrow-down',
  vk: 'bi-play-btn',
  soundcloud: 'bi-music-note-beamed',
  pinterest: 'bi-pinterest'
};

export default {
  name: 'UploadUrlModal',

  props: {
    show: { type: Boolean, default: false }
  },

  emits: ['close', 'uploaded'],

  data() {
    return {
      url: '',
      title: '',
      submitting: false,
      submitSuccess: false,
      errorMessage: '',
      materialId: null,
      queueId: null,
      sources: FALLBACK_SOURCES
    };
  },

  computed: {
    ...mapGetters('app', ['configData']),

    serviceName() {
      return this.configData?.chatbot_name || this.configData?.service_name || 'vid2text';
    },

    // Подсветка совпавшего источника — человек видит, что ссылку узнали,
    // ещё до отправки.
    detectedService() {
      const u = this.url.toLowerCase();
      if (!u) return null;
      if (u.includes('disk.yandex.')) return 'yandex';
      if (u.includes('pinterest.') || u.includes('pin.it')) return 'pinterest';
      if (u.includes('vk.com') || u.includes('vk.ru') || u.includes('vkvideo.ru')) return 'vk';
      if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
      if (u.includes('soundcloud.com')) return 'soundcloud';
      return null;
    }
  },

  watch: {
    show(isOpen) {
      if (isOpen) {
        this.reset();
        this.loadSources();
        this.$nextTick(() => this.$refs.urlInput?.focus());
      }
    }
  },

  methods: {
    reset() {
      this.url = '';
      this.title = '';
      this.submitting = false;
      this.submitSuccess = false;
      this.errorMessage = '';
      this.materialId = null;
      this.queueId = null;
    },

    sourceIcon(key) {
      return SOURCE_ICONS[key] || 'bi-link-45deg';
    },

    async loadSources() {
      try {
        const response = await axiosConfig.get('', { params: { action: 'url_sources' } });
        const list = response.data?.sources || response.data?.data?.sources;
        if (Array.isArray(list) && list.length) {
          this.sources = list;
        }
      } catch (e) {
        // Витрина не критична — остаёмся на запасном списке.
      }
    },

    async submit() {
      if (this.submitting || !this.url.trim()) return;

      this.submitting = true;
      this.errorMessage = '';

      const formData = new FormData();
      formData.append('action', 'submit_url');
      formData.append('url', this.url.trim());
      if (this.title.trim()) formData.append('title', this.title.trim());

      try {
        // chatbot_name в query — этого требует initChatBotTokens на бэкенде
        const endpoint = `${API_URL}?chatbot_name=${encodeURIComponent(this.serviceName)}`;
        const response = await axiosConfig.post(endpoint, formData);
        const data = response.data?.data || response.data;

        if (data?.success === false || data?.error) {
          this.errorMessage = data.error || this.$t('upload_url.generic_error');
          return;
        }

        this.materialId = data?.material_id || null;
        this.queueId = data?.queue_id || null;
        this.submitSuccess = true;
        this.$emit('uploaded', { materialId: this.materialId, queueId: this.queueId });
      } catch (e) {
        this.errorMessage = e.response?.data?.error
          || e.response?.data?.message
          || this.$t('upload_url.generic_error');
      } finally {
        this.submitting = false;
      }
    },

    goToMaterial() {
      if (!this.materialId) return;
      this.$router.push(`/article/${this.materialId}`);
      this.$emit('close');
    },

    closeModal() {
      if (this.submitting) return;
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.url-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Выше липкой шапки материала и боковой панели: в приложении встречаются
     слои до 100000, окно обязано быть поверх */
  z-index: 100001;
  padding: 16px;
}

.url-modal {
  background: var(--bs-body-bg, #fff);
  color: var(--bs-body-color, #212529);
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, .25);
}

.url-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 12px;
  border-bottom: 1px solid rgba(128, 128, 128, .2);
}

.url-modal-header h3 {
  margin: 0;
  font-size: 1.15rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1rem;
  line-height: 1;
  color: inherit;
  opacity: .6;
  cursor: pointer;
}

.close-btn:hover { opacity: 1; }

.url-modal-body { padding: 18px 20px 20px; }

.url-hint {
  font-size: .9rem;
  opacity: .75;
  margin-bottom: 14px;
}

.url-input { font-size: .95rem; }

.sources-block { margin-bottom: 16px; }

.sources-caption {
  font-size: .8rem;
  text-transform: uppercase;
  letter-spacing: .03em;
  opacity: .6;
  margin-bottom: 8px;
}

/* Плашки в строку: рамка во всю ширину читается как поле ввода — рядом с
   настоящим полем ссылки это сбивает. Пример адреса ушёл в подсказку title. */
.sources-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.source-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(128, 128, 128, .12);
  font-size: .82rem;
  line-height: 1.4;
  cursor: default;
  transition: background-color .15s, color .15s;
}

.source-chip i { font-size: .9rem; opacity: .8; }

.source-chip--matched {
  background: var(--bs-primary, #0d6efd);
  color: #fff;
}

.source-chip--matched i { opacity: 1; }

.source-title { font-weight: 500; }

.url-error { font-size: .88rem; padding: 8px 12px; }

.url-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.url-success-state {
  text-align: center;
  padding: 24px 0 8px;
}

.success-icon {
  font-size: 2.5rem;
  color: var(--bs-success, #198754);
  margin-bottom: 10px;
}

.success-text { font-size: .95rem; }
</style>
